import { useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Quote, QuoteType, LifecycleStage } from '../types/admin';

export type QuoteSortField = 'createdAt' | 'customerCompanyName' | 'quoteStatus' | 'movingDate' | 'estimateVisitDate';
export type SortDir = 'asc' | 'desc';

export interface QuoteStats {
  draft: number;
  sent: number;
  response_received: number;
  total: number;
}

interface ListResponse {
  success: boolean;
  data: Quote[];
  hasMore: boolean;
  nextCursor?: string;
  stats: QuoteStats;
}

export interface UseQuotesParams {
  search?: string;
  sortBy?: QuoteSortField;
  sortDir?: SortDir;
  leadUid?: string;
  type?: QuoteType;
  lifecycleStage?: LifecycleStage;
}

const PAGE_SIZE = 20;

function buildQuery(params: UseQuotesParams, cursor?: string): string {
  const qs = new URLSearchParams();
  qs.set('limit', String(PAGE_SIZE));
  if (cursor) qs.set('cursor', cursor);
  if (params.search) qs.set('search', params.search);
  if (params.sortBy) qs.set('sortBy', params.sortBy);
  if (params.sortDir) qs.set('sortDir', params.sortDir);
  if (params.leadUid) qs.set('leadUid', params.leadUid);
  if (params.type) qs.set('type', params.type);
  if (params.lifecycleStage) qs.set('lifecycleStage', params.lifecycleStage);
  return qs.toString();
}

/**
 * Fetches and manages a paginated list of quotes for an org.
 * Supports search, sort, filtering by lead, and infinite scroll.
 * Uses React Query for caching and deduplication.
 */
export function useQuotes(orgId: string | null, params: UseQuotesParams = {}) {
  const queryClient = useQueryClient();
  const { search, sortBy = 'createdAt', sortDir = 'desc', leadUid, type, lifecycleStage } = params;
  const resolvedParams = { search, sortBy, sortDir, leadUid, type, lifecycleStage };

  const queryKey = ['quotes', orgId, resolvedParams] as const;

  const {
    data,
    isLoading,
    isFetchingNextPage,
    error: queryError,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const qs = buildQuery(resolvedParams, pageParam as string | undefined);
      return api.get<ListResponse>(`/quotes/${orgId}?${qs}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor : undefined,
    enabled: !!orgId,
  });

  // Flatten pages into a single list.
  const quotes = data?.pages.flatMap(p => p.data) ?? [];
  // Stats come from the first page (backend computes them across all docs).
  const stats = data?.pages[0]?.stats ?? { draft: 0, sent: 0, response_received: 0, total: 0 };

  /**
   * Create a new move record from a `NewMoveFormData`-shaped payload.
   * Used by the shared NewMoveForm component. The caller (NewMoveForm) is
   * responsible for computing the `lifecycleStage` — this hook just posts
   * whatever it receives.
   */
  const createMoveMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (!orgId) throw new Error('No active org');
      const res = await api.post<{ success: boolean; data: Quote }>(`/quotes/${orgId}`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes', orgId] });
      queryClient.invalidateQueries({ queryKey: ['leads', orgId] });
    },
  });

  const createMove = useCallback(
    async (payload: Record<string, unknown>): Promise<Quote> => {
      return createMoveMutation.mutateAsync(payload);
    },
    [createMoveMutation],
  );

  const deleteMutation = useMutation({
    mutationFn: async (quoteId: string) => {
      if (!orgId) return;
      await api.del(`/quotes/${orgId}/${quoteId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes', orgId] });
    },
  });

  const deleteQuote = useCallback(
    async (quoteId: string) => {
      await deleteMutation.mutateAsync(quoteId);
    },
    [deleteMutation],
  );

  const fetchMore = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    quotes,
    loading: isLoading,
    loadingMore: isFetchingNextPage,
    error: queryError ? (queryError instanceof Error ? queryError.message : 'Failed to load quotes') : null,
    hasMore: !!hasNextPage,
    stats,
    createMove,
    deleteQuote,
    fetchMore,
    refetch,
  };
}
